import {
  axiosConfig,
  del,
  get,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  EventRegistration,
  EventRegistrationPayload,
} from "@project/types/event";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/event-registrations`;
const SILENT: RequestConfig = { showNotification: false };

function appendIfPresent(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null || value === "") {
    return;
  }
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  fd.append(key, String(value));
}

function toFormData(payload: Partial<EventRegistrationPayload>): FormData {
  const fd = new FormData();
  appendIfPresent(fd, "event", payload.event);
  appendIfPresent(fd, "full_name", payload.full_name);
  appendIfPresent(fd, "mobile_number", payload.mobile_number);
  appendIfPresent(fd, "email", payload.email);
  appendIfPresent(fd, "total_attendees", payload.total_attendees);
  appendIfPresent(fd, "notes", payload.notes);
  if (payload.status) {
    appendIfPresent(fd, "status", payload.status);
  }

  if (payload.image instanceof File) {
    fd.append("image", payload.image);
  }
  return fd;
}

class EventRegistrationService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<EventRegistration>> =>
    fetchListPage<EventRegistration>(`${BASE}/`, query);

  get = async (id: number): Promise<EventRegistration | undefined> => {
    const data = await get<
      ApiItemResponse<EventRegistration> | EventRegistration
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: EventRegistrationPayload
  ): Promise<EventRegistration> => {
    const response = await axiosConfig.post<
      ApiItemResponse<EventRegistration> | EventRegistration
    >(`${BASE}/`, toFormData(payload), {
      ...SILENT,
      headers: { "Content-Type": "multipart/form-data" },
    });
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to register for event.");
    }
    return item;
  };

  update = async (
    id: number | string,
    payload: Partial<EventRegistrationPayload>
  ): Promise<EventRegistration | undefined> => {
    const response = await axiosConfig.patch<
      ApiItemResponse<EventRegistration> | EventRegistration
    >(`${BASE}/${id}/`, toFormData(payload), {
      ...SILENT,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return unwrapItem(response.data);
  };

  delete = async (id: number | string): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };
}

export const eventRegistrationService = new EventRegistrationService();
